/**
 * DOM overlay screens: title, setup, round-over, game-over.
 * The arena keeps rendering on the canvas behind; countdown and play show no
 * overlay. Screens emit intents; `App` turns them into flow events.
 */

import { MAX_PLAYERS, MIN_PLAYERS, VICTORY_SCORE_CHOICES } from "../game/config";
import type { AiLevelName } from "../ai/levels";
import type { AppState } from "../game/flow";
import type { Match } from "../sim/match";
import { COLOR_HEX, PlayerState } from "../sim/types";

export interface MatchSettings {
  players: number;
  humans: number;
  aiLevel: AiLevelName;
  victoryScore: number;
}

export type ScreenIntent =
  | { type: "advance" }
  | { type: "start"; settings: MatchSettings }
  | { type: "toTitle" };

const DEFAULT_SETTINGS: MatchSettings = {
  players: 3,
  humans: 1,
  aiLevel: "normal",
  victoryScore: 10,
};

const MAX_KEYBOARD_HUMANS = 4;
const AI_LEVELS: readonly AiLevelName[] = ["easy", "normal", "hard"];

export class Screens {
  onIntent: (intent: ScreenIntent) => void = () => {};
  readonly settings: MatchSettings = { ...DEFAULT_SETTINGS };

  private readonly root: HTMLElement;

  constructor(mount: HTMLElement) {
    this.root = document.createElement("div");
    this.root.className = "overlay";
    mount.appendChild(this.root);
    this.root.addEventListener("click", (e) => this.onClick(e));
  }

  show(state: AppState, match: Match | null): void {
    switch (state) {
      case "title":
        this.root.hidden = false;
        this.root.innerHTML = titleHtml();
        break;
      case "setup":
        this.root.hidden = false;
        this.root.innerHTML = setupHtml(this.settings);
        break;
      case "roundOver":
        this.root.hidden = false;
        this.root.innerHTML = roundOverHtml(match);
        break;
      case "gameOver":
        this.root.hidden = false;
        this.root.innerHTML = gameOverHtml(match);
        break;
      case "countdown":
      case "playing":
        this.root.hidden = true;
        this.root.innerHTML = "";
        break;
    }
  }

  private onClick(e: Event): void {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-action]");
    if (!el) return;

    switch (el.dataset.action) {
      case "advance":
        this.onIntent({ type: "advance" });
        break;
      case "toTitle":
        this.onIntent({ type: "toTitle" });
        break;
      case "start":
        this.onIntent({ type: "start", settings: { ...this.settings } });
        break;
      case "players":
        this.settings.players = clamp(Number(el.dataset.value), MIN_PLAYERS, MAX_PLAYERS);
        this.clampHumans();
        this.show("setup", null);
        break;
      case "humans":
        this.settings.humans = Number(el.dataset.value);
        this.clampHumans();
        this.show("setup", null);
        break;
      case "ai":
        this.settings.aiLevel = el.dataset.value as AiLevelName;
        this.show("setup", null);
        break;
      case "victory":
        this.settings.victoryScore = Number(el.dataset.value);
        this.show("setup", null);
        break;
    }
  }

  private clampHumans(): void {
    const max = Math.min(MAX_KEYBOARD_HUMANS, this.settings.players);
    this.settings.humans = clamp(this.settings.humans, 1, max);
  }
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function chip(label: string | number, on: boolean, action: string, value: string | number): string {
  return `<button class="chip ${on ? "on" : ""}" data-action="${action}" data-value="${value}">${label}</button>`;
}

function titleHtml(): string {
  return `
    <div class="card">
      <h1>TRON</h1>
      <p class="sub">The Grid</p>
      <p class="tag">A 2013 C/SDL university project, rebuilt.</p>
      <button class="btn primary" data-action="advance">Play</button>
      <p class="hint">Press Enter</p>
      <a class="story-link" href="./retrospective">The story — 2013 → 2026 →</a>
    </div>`;
}

function setupHtml(s: MatchSettings): string {
  const maxHumans = Math.min(MAX_KEYBOARD_HUMANS, s.players);
  const players = range(MIN_PLAYERS, MAX_PLAYERS)
    .map((n) => chip(n, n === s.players, "players", n))
    .join("");
  const humans = range(1, maxHumans)
    .map((n) => chip(n, n === s.humans, "humans", n))
    .join("");
  const levels = AI_LEVELS.map((l) =>
    chip(l[0]!.toUpperCase() + l.slice(1), l === s.aiLevel, "ai", l),
  ).join("");
  const victory = VICTORY_SCORE_CHOICES.map((n) =>
    chip(n, n === s.victoryScore, "victory", n),
  ).join("");
  const aiCount = s.players - s.humans;

  return `
    <div class="card">
      <h2>Setup</h2>
      <label>Players</label>
      <div class="chips">${players}</div>
      <label>Humans <span class="muted">(${aiCount} AI)</span></label>
      <div class="chips">${humans}</div>
      <label>AI level</label>
      <div class="chips">${levels}</div>
      <label>Rounds to win</label>
      <div class="chips">${victory}</div>
      <button class="btn primary" data-action="start">Start</button>
      <button class="btn ghost" data-action="toTitle">Back</button>
    </div>`;
}

function scoreRows(match: Match | null): string {
  if (!match) return "";
  return match.players
    .map((p) => {
      const [stroke] = COLOR_HEX[p.color];
      const dead = p.state === PlayerState.Dead;
      return `<div class="score-row">
        <span class="swatch" style="background:${dead ? "#33475a" : stroke}"></span>
        <span>Player ${p.index + 1}${p.isAI ? ' <span class="muted">AI</span>' : ""}</span>
        <span class="score">${match.scores[p.index] ?? 0}</span>
      </div>`;
    })
    .join("");
}

function roundOverHtml(match: Match | null): string {
  const title =
    typeof match?.roundWinner === "number"
      ? `Player ${match.roundWinner + 1} wins the round`
      : "Draw";
  return `
    <div class="card">
      <h2>${title}</h2>
      <div class="scores">${scoreRows(match)}</div>
      <button class="btn primary" data-action="advance">Next round</button>
      <p class="hint">Press Enter</p>
    </div>`;
}

function gameOverHtml(match: Match | null): string {
  const title =
    typeof match?.matchWinner === "number" ? `Player ${match.matchWinner + 1} wins!` : "Match over";
  return `
    <div class="card">
      <h1>${title}</h1>
      <div class="scores">${scoreRows(match)}</div>
      <button class="btn primary" data-action="advance">Play again</button>
      <button class="btn ghost" data-action="toTitle">Menu</button>
    </div>`;
}

function range(lo: number, hi: number): number[] {
  return Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
}
